import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	TextInput,
	AutoOptionInputFreeSolo,
	disableEnterKey,
	ResetButton,
	SubmitButton,
	TextBackground,
} from "../components";
import { prefixMap, prefixError, mhcChoices, BACKEND_BASE_URL } from "../constants";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";

import { MhcAlleles, MhcAlleleOptions } from "./MHC_alleles"; // Adjust path as needed

/**
 * This schema defines allowed responses for various inputs in the STEGG
 * form. Users are shown validation errors if their responses do not match
 * this schema.
 */
const APEGenFormSchema = z
	.object({
		TCRalpha: z.string()
		.refine((TCRalpha: string) => {
			let noLower = TCRalpha.replace(/[a-z]/g, "");
			return /^[ACDEFGHIKLMNPQRSTYWV]*$/g.test(noLower);
		}, "Invalid characters in TCR alpha chain. Must not contain any of { B, J, O, U, X, Z }."),
		TCRbeta: z.string()
		.refine((TCRbeta: string) => {
			let noLower = TCRbeta.replace(/[a-z]/g, "");
			return /^[ACDEFGHIKLMNPQRSTYWV]*$/g.test(noLower);
		}, "Invalid characters in TCR beta chain. Must not contain any of { B, J, O, U, X, Z }."),
		/** Peptide validation */
		peptide: z
			.string()
			/**
			 * Validate peptide length.
			 */
			.refine((peptide: string) => {
				let noLower = peptide.replace(/[a-z]/g, "");
				return !(noLower.length < 8 || noLower.length > 14);
			}, "Invalid peptide length; must be in range [8, 14].")
			/**
			 * Validate characters in peptide.
			 */
			.refine((peptide: string) => {
				let noLower = peptide.replace(/[a-z]/g, "");
				return /^[ACDEFGHIKLMNPQRSTYWV]*$/g.test(noLower);
			}, "Invalid characters in peptide. Must not contain any of { B, J, O, U, X, Z }."),
		mhc: z.string()
		.refine((TCRalpha: string) => {
			let noLower = TCRalpha.replace(/[a-z]/g, "");
			return /^[ACDEFGHIKLMNPQRSTYWV]*$/g.test(noLower);
		}, "Invalid characters in MHC chain. Must not contain any of { B, J, O, U, X, Z }."),
		email: z.string().email(),
	})
	/**
	 * Validate PTMs for peptide input.
	 */
	.superRefine((args, ctx) => {
		// Split peptide into different substrings based on each capital letter.
		let sequenceList = args.peptide
			.replace(/[A-Z]/g, (match: string) => match + " ")
			.split(/\s+/);
		for (let i = 0; i < sequenceList.length; i++) {
			if (sequenceList[i].length >= 2) {
				let aminoAcid = sequenceList[i];
				// Check prefix first.
				if (prefixMap[aminoAcid[0]] !== undefined) {
					// Check the amino acid next.
					if (prefixMap[aminoAcid[0]].indexOf(aminoAcid) === -1) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							path: ["peptide"],
							fatal: true,
							message: prefixError(aminoAcid[0]),
						});
					}
				} else {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ["peptide"],
						fatal: true,
						message: "Wrong PTM prefix, check PTM notation.",
					});
				}
			}
		}
	});

/** Fields are directly inferred from `APEGenFormSchema`. */
type APEGenFormFields = z.infer<typeof APEGenFormSchema>;

/** Default values on initialization or reset. */
const defaultValues: APEGenFormFields = {
	TCRalpha: "",
	TCRbeta: "",
	peptide: "",
	mhc: "",
	email: "",
};

/**
 * Define the HTML for the Form page.
 * @returns HTML for the Form page
 */
export const APEGenForm = () => {
	const methods = useForm<APEGenFormFields>({
		// Validate inputs against `APEGenFormSchema`.
		resolver: zodResolver(APEGenFormSchema),
		defaultValues: defaultValues,
	});
	/**
	 * Unpack necessary functions from `methods` (React Hook Form function).
	 */
	const { handleSubmit, resetField, watch } = methods;
	const navigate = useNavigate();

	/**
	 * Define behavior after submitting the form.
	 * @param form Fields in `APEGenFormFields`
	 */
	const onSubmit: SubmitHandler<APEGenFormFields> = async (
		form: APEGenFormFields
	) => {
		try {
			const { email, ...steggParams } = form;
			const response = await fetch(`${BACKEND_BASE_URL}/stegg/job/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({stegg_params: steggParams, email: email}),
			});

			if (!response.ok) {
				throw new Error(
					"Submitting a job failed: not able to initialize the job with input parameters."
				);
			}
			const responseData = await response.json();
			const job_id = responseData.id;

			// Start the job
			await fetch(`${BACKEND_BASE_URL}/stegg/job/${job_id}/run/`, {
				method: "POST",
			});
			navigate(`/task_status/${job_id}`);
			// console.log({stegg_params: steggParams, email: email});
			alert(
				`STEGG job submitted successfully - you will receive an email soon at ${form.email}`
			);
		} catch (error) {
			alert(`Failed to submit job. Please try again with another browser.`);
		}
	};

	/**
	 * Build the form using components from `components.tsx`.
	 * Do not use non-parameterized components.
	 */
	return (
		<Grid container
			alignItems="center"
			justifyContent="center">
	
			<Grid item xs={3} sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}}>
				<img src={require("../STEGG_logo.png")} width="100%"></img>
			</Grid>
			<Grid xs={6}>
			<FormProvider {...methods}>
				<section className="page">
					<TextBackground>
						<form
							onSubmit={handleSubmit(onSubmit)}
							onKeyDown={(e) => disableEnterKey(e)}
						>
							<h2>Welcome to the STEGG modeling tool</h2>
							{/** All field inputs go here. */}

							{/** TCRalpha section */}
							<TextInput<APEGenFormFields>
								name="TCRalpha"
								label="TCR alpha chain"
								helperText={
									<ol>
										<li>
											Must contain only the following letters:
											&#123; A, C, D, E, F, G, H, I, K, L, M,
											N, P, Q, R, S, T, V, W, Y &#125;
										</li>
										<li>Input will be verified with ANARCI &#123; see: https://github.com/oxpig/ANARCI &#125; </li>
									</ol>
								}
							/>

							{/** TCRbeta section */}
							<TextInput<APEGenFormFields>
								name="TCRbeta"
								label="TCR beta chain"
								helperText={
									<ol>
										<li>
											Must contain only the following letters:
											&#123; A, C, D, E, F, G, H, I, K, L, M,
											N, P, Q, R, S, T, V, W, Y &#125;
										</li>
										<li>Input will be verified with ANARCI &#123; see: https://github.com/oxpig/ANARCI &#125; </li>
									</ol>
								}
							/>

							{/** Peptide section */}
							<TextInput<APEGenFormFields>
								name="peptide"
								label="Peptide"
								helperText={
									<ol>
										<li>
											Must contain only the following letters:
											&#123; A, C, D, E, F, G, H, I, K, L, M,
											N, P, Q, R, S, T, V, W, Y &#125;
										</li>
										<li>Required peptide length: [8, 14]</li>
										<li>
											Allowed PTM prefixes: &#123;pS, pT, pY,
											nC, nP, nY, nW, aK, cK, cR, mmK, mdK,
											mtK, xhC, xoC, xdC, oM&#125;
										</li>
									</ol>
								}
							/>
	
							{/** MHC section */}
							<AutoOptionInputFreeSolo<APEGenFormFields>
							name="mhc"
							label="MHC I heavy/alpha chain"
							options={MhcAlleleOptions} 
							optionsMap={MhcAlleles} 
							helperText={
								<ol>
									<li>
										**Option 1: Allele Auto-fill.** Click and wait for the dropdown menu, then select from suggestions to auto-fill the field with the known sequence.
									</li>
									<li>
										**Option 2: Free Text Input.** Alternatively, you may type or paste a custom MHC sequence.
									</li>
									<li>
										The sequence must contain only the following letters:
										&#123; A, C, D, E, F, G, H, I, K, L, M, N, P, Q, R, S, T, V, W, Y &#125;
									</li>
								</ol>
							}
							/>
	
							{/** Email section */}
							<TextInput<APEGenFormFields>
								name="email"
								label="Email"
								helperText="Your email address will only be used to send you a link to the TCR-pMHC modelling results."
							/>

							{/** Button to reset the entire form to original default values. */}
							<ResetButton />
	
							{/** Submit button */}
							<SubmitButton<APEGenFormFields> onSubmit={onSubmit} />
						</form>
					</TextBackground>
				</section>
			</FormProvider>
		</Grid>
		<Grid item xs={3} sx={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
		}}>
			<img src={require("../main_pic.png")} width="80%"></img>
		</Grid>
		</Grid>
	);
};
